// ============================================================================
// FREE AGENCY BOARD
// ============================================================================
import React, { useState, useMemo, useCallback } from 'react';
import { getPlayerRating, getRatingColor } from './playerRatings';
import { generateOffers, getUserSigningOptions, calculateOfferTotal } from './contractMarket';
import { CBA } from './tradeEngine';

const formatSalary = (n) => {
  if (!n || n === 0) return '$0';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  return '$' + (n / 1000).toFixed(0) + 'K';
};
const formatSalaryFull = (n) => {
  if (!n || n === 0) return '$0';
  return '$' + n.toLocaleString();
};

const TEAMS_MAP = {
  ATL:'Hawks',BOS:'Celtics',BKN:'Nets',CHA:'Hornets',CHI:'Bulls',CLE:'Cavaliers',DAL:'Mavericks',DEN:'Nuggets',DET:'Pistons',GSW:'Warriors',HOU:'Rockets',IND:'Pacers',LAC:'Clippers',LAL:'Lakers',MEM:'Grizzlies',MIA:'Heat',MIL:'Bucks',MIN:'Timberwolves',NOP:'Pelicans',NYK:'Knicks',OKC:'Thunder',ORL:'Magic',PHI:'76ers',PHX:'Suns',POR:'Trail Blazers',SAC:'Kings',SAS:'Spurs',TOR:'Raptors',UTA:'Jazz',WAS:'Wizards'
};
const teamName = (a) => TEAMS_MAP[a] || a;
const teamLogo = (a) => {
  const ids = {ATL:1610612737,BOS:1610612738,BKN:1610612751,CHA:1610612766,CHI:1610612741,CLE:1610612739,DAL:1610612742,DEN:1610612743,DET:1610612765,GSW:1610612744,HOU:1610612745,IND:1610612754,LAC:1610612746,LAL:1610612747,MEM:1610612763,MIA:1610612748,MIL:1610612749,MIN:1610612750,NOP:1610612740,NYK:1610612752,OKC:1610612760,ORL:1610612753,PHI:1610612755,PHX:1610612756,POR:1610612757,SAC:1610612758,SAS:1610612759,TOR:1610612761,UTA:1610612762,WAS:1610612764};
  return `https://cdn.nba.com/logos/nba/${ids[a]}/global/L/logo.svg`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FreeAgencyBoard = ({ freeAgentPool, salaryData, myTeam, onSignPlayer, onAdvanceSeason, onBack, isMidSeason, seasonYear }) => {
  const [selectedFA, setSelectedFA] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('ovr');
  const [offerModal, setOfferModal] = useState(null);
  const [signedLog, setSignedLog] = useState([]);
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState(false);

  const nextYearLabel = seasonYear ? `${seasonYear + 1}-${String(seasonYear + 2).slice(2)}` : '2026-27';

  // Generate offers for all FAs (memoized)
  const faWithOffers = useMemo(() => {
    return freeAgentPool.map(fa => {
      const offers = generateOffers(fa, salaryData, myTeam);
      return { ...fa, aiOffers: offers };
    });
  }, [freeAgentPool, salaryData, myTeam]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = [...faWithOffers];
    if (filter !== 'all') {
      list = list.filter(fa => fa.position === filter);
    }
    list.sort((a, b) => {
      switch (sortBy) {
        case 'ovr': return (b.ovr || 0) - (a.ovr || 0);
        case 'salary': return (b.priorSalary || 0) - (a.priorSalary || 0);
        case 'offers': return (b.aiOffers?.offers?.length || 0) - (a.aiOffers?.offers?.length || 0);
        default: return 0;
      }
    });
    return list;
  }, [faWithOffers, filter, sortBy]);

  // Signing tools for the ACTIVE team
  const signingOptions = useMemo(() => {
    return getUserSigningOptions(myTeam, salaryData);
  }, [myTeam, salaryData]);

  const handleSign = useCallback((faName, salary, years) => {
    onSignPlayer(faName, salary, years, myTeam);
    setSignedLog(prev => [...prev, faName]);
    setOfferModal(null);
    setSelectedFA(null);
  }, [onSignPlayer, myTeam]);

  return (
    <div className="fa-board fade-in">
      <div className="fa-board-header">
        <div>
          <div className="fa-board-label">Free Agency</div>
          <div className="fa-board-title">{isMidSeason ? 'Available Free Agents' : 'Offseason Free Agency'}</div>
        </div>
        <div className="fa-board-actions">
          {onBack && <button className="fa-back-btn" onClick={onBack}>Back</button>}
          {onAdvanceSeason && (
            <button className="recap-fa-btn" onClick={() => setShowAdvanceConfirm(true)}>
              Advance to Next Season &rarr;
            </button>
          )}
        </div>
      </div>

      {/* Active team's cap situation */}
      <div className="fa-cap-ribbon">
        <div className="fa-cap-item">
          <div className="fa-cap-label">
            <img src={teamLogo(myTeam)} alt="" style={{ width: 16, height: 16, verticalAlign: 'middle', marginRight: 4 }} />
            {teamName(myTeam)} Payroll
          </div>
          <div className="fa-cap-value">{formatSalary(signingOptions.payroll)}</div>
        </div>
        <div className="fa-cap-item">
          <div className="fa-cap-label">Cap Space</div>
          <div className={`fa-cap-value ${signingOptions.capSpace > 0 ? 'green' : 'red'}`}>
            {signingOptions.capSpace > 0 ? formatSalary(signingOptions.capSpace) : 'Over Cap'}
          </div>
        </div>
        <div className="fa-cap-item">
          <div className="fa-cap-label">Available Tools</div>
          <div className="fa-cap-tools">
            {signingOptions.tools.map(t => (
              <span key={t.type} className="fa-tool-badge">{t.label}</span>
            ))}
          </div>
        </div>
        <div className="fa-cap-item">
          <div className="fa-cap-label">Free Agents</div>
          <div className="fa-cap-value">{filtered.length}</div>
        </div>
      </div>

      {/* Signed log */}
      {signedLog.length > 0 && (
        <div className="fa-signed-log">
          Signed this offseason: {signedLog.join(', ')}
        </div>
      )}

      {/* Filters */}
      <div className="fa-filters">
        <div className="fa-filter-group">
          {['all', 'PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
            <button
              key={pos}
              className={`fa-filter-btn ${filter === pos ? 'active' : ''}`}
              onClick={() => setFilter(pos)}
            >
              {pos === 'all' ? 'All' : pos}
            </button>
          ))}
        </div>
        <div className="fa-filter-group">
          <select className="fa-sort-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="ovr">Sort: OVR</option>
            <option value="salary">Sort: Prior Salary</option>
            <option value="offers">Sort: Most Interest</option>
          </select>
        </div>
      </div>

      {/* Free Agent List */}
      <div className="fa-list">
        {filtered.map(fa => {
          const ovr = fa.ovr || getPlayerRating(fa.name) || 70;
          const rColor = getRatingColor(ovr);
          const bestOffer = fa.aiOffers?.offers?.[0];
          const isExpanded = selectedFA === fa.name;
          const wasSigned = signedLog.includes(fa.name);

          if (wasSigned) return null;

          return (
            <div key={fa.name} className={`fa-card ${isExpanded ? 'expanded' : ''}`}>
              <div className="fa-card-main" onClick={() => setSelectedFA(isExpanded ? null : fa.name)}>
                <div className="fa-card-left">
                  <span className="ovr-badge" style={{ borderLeftColor: rColor }}>{ovr}</span>
                  <div>
                    <div className="fa-card-name">{fa.name}</div>
                    <div className="fa-card-meta">
                      {fa.position} · {fa.lastTeam ? `from ${teamName(fa.lastTeam)}` : 'Free Agent'}
                      {fa.reason === 'waived' && ' · Waived'}
                      {fa.reason === 'waived-stretched' && ' · Waived (Stretched)'}
                      {fa.reason === 'buyout' && ' · Buyout'}
                      {fa.reason === 'expired' && ' · Expired'}
                    </div>
                  </div>
                </div>
                <div className="fa-card-right">
                  {bestOffer ? (
                    <div className="fa-card-market">
                      <div className="fa-card-market-value">{formatSalary(bestOffer.salary)}/yr</div>
                      <div className="fa-card-market-label">
                        {fa.aiOffers.offers.length} offer{fa.aiOffers.offers.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ) : (
                    <div className="fa-card-market">
                      <div className="fa-card-market-value" style={{ color: 'var(--text-dim)' }}>No offers</div>
                      <div className="fa-card-market-label">Available</div>
                    </div>
                  )}
                  <span className="fa-card-chevron">{isExpanded ? '\u25B2' : '\u25BC'}</span>
                </div>
              </div>

              {/* Expanded: show AI offers + sign button */}
              {isExpanded && (
                <div className="fa-card-detail">
                  {/* Market value context */}
                  <div className="fa-detail-section">
                    <div className="fa-detail-title">Market Value</div>
                    <div className="fa-market-range">
                      <div className="fa-market-bar">
                        <div className="fa-market-fill" style={{
                          left: '0%',
                          width: `${Math.min(100, (fa.aiOffers.market.mid / fa.aiOffers.market.ceiling) * 100)}%`,
                          background: rColor,
                          opacity: 0.3,
                        }} />
                      </div>
                      <div className="fa-market-labels">
                        <span>Low: {formatSalary(fa.aiOffers.market.low)}</span>
                        <span>Mid: {formatSalary(fa.aiOffers.market.mid)}</span>
                        <span>High: {formatSalary(fa.aiOffers.market.high)}</span>
                      </div>
                    </div>
                    {fa.priorSalary > 0 && (
                      <div className="fa-prior-salary">Prior salary: {formatSalaryFull(fa.priorSalary)}</div>
                    )}
                  </div>

                  {/* AI Offers */}
                  {fa.aiOffers.offers.length > 0 && (
                    <div className="fa-detail-section">
                      <div className="fa-detail-title">
                        Competing Offers
                        <span className="fa-detail-hint"> — you must match or beat to sign</span>
                      </div>
                      {fa.aiOffers.offers.map((offer, i) => (
                        <div key={i} className="fa-offer-row">
                          <div className="fa-offer-team">
                            <img src={teamLogo(offer.team)} alt="" className="fa-offer-logo" />
                            <span>{teamName(offer.team)}</span>
                          </div>
                          <div className="fa-offer-terms">
                            <span className="fa-offer-salary">{formatSalary(offer.salary)}/yr</span>
                            <span className="fa-offer-years">{offer.years}yr · {formatSalary(offer.total)} total</span>
                          </div>
                          <div className="fa-offer-via">{offer.via}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Sign Button */}
                  <button
                    className="fa-sign-btn"
                    onClick={() => setOfferModal(fa)}
                  >
                    Make Offer
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Offer Modal */}
      {offerModal && (
        <OfferModal
          fa={offerModal}
          signingOptions={signingOptions}
          bestOffer={offerModal.aiOffers?.offers?.[0]}
          onSign={handleSign}
          onClose={() => setOfferModal(null)}
        />
      )}

      {/* Advance Season Confirmation */}
      {showAdvanceConfirm && onAdvanceSeason && (
        <div className="modal-overlay" onClick={() => setShowAdvanceConfirm(false)}>
          <div className="advance-season-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAdvanceConfirm(false)}>&times;</button>
            <div className="advance-season-accent" />
            <div className="advance-season-body">
              <div className="advance-season-label">Season Advance</div>
              <div className="advance-season-title">Advance to the {nextYearLabel} season?</div>
              <div className="advance-season-sub">
                All contracts will roll forward one year. Expired contracts will be removed. Team and player options will be exercised. A new 82-game schedule will be generated.
              </div>
              <div className="advance-season-details">
                <span>Contracts shift forward one year</span>
                <span>Expired players removed from rosters</span>
                <span>Drafted rookies join their teams</span>
                <span>New season begins at Regular Season phase</span>
              </div>
            </div>
            <div className="advance-season-actions">
              <button className="waive-cancel-btn" onClick={() => setShowAdvanceConfirm(false)}>Cancel</button>
              <button className="fa-offer-confirm-btn" onClick={() => { setShowAdvanceConfirm(false); onAdvanceSeason(); }}>
                Start {nextYearLabel} Season
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// OFFER MODAL — User builds their contract offer
// ============================================================================
const OfferModal = ({ fa, signingOptions, bestOffer, onSign, onClose }) => {
  const bestTool = signingOptions.tools[0]; // Best available tool
  const [salary, setSalary] = useState(() => {
    // Default to matching best offer or market mid
    if (bestOffer) return Math.min(bestOffer.salary, bestTool?.maxSalary || bestOffer.salary);
    return Math.min(fa.aiOffers?.market?.mid || 2100000, bestTool?.maxSalary || 2461463);
  });
  const [years, setYears] = useState(() => {
    if (bestOffer) return Math.min(bestOffer.years, bestTool?.maxYears || 2);
    return 1;
  });
  const [selectedTool, setSelectedTool] = useState(bestTool?.type || 'vetMin');

  const activeTool = signingOptions.tools.find(t => t.type === selectedTool) || signingOptions.tools[0];
  const maxSalary = activeTool?.maxSalary || 2461463;
  const maxYears = activeTool?.maxYears || 2;
  const clampedSalary = Math.min(salary, maxSalary);
  const clampedYears = Math.min(years, maxYears);

  const total = calculateOfferTotal(clampedSalary, clampedYears);
  const bestOfferTotal = bestOffer ? calculateOfferTotal(bestOffer.salary, bestOffer.years) : 0;
  const willSign = !bestOffer || total >= bestOfferTotal;

  const ovr = fa.ovr || getPlayerRating(fa.name) || 70;
  const rColor = getRatingColor(ovr);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="fa-offer-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        <div className="fa-offer-modal-header">
          <div className="fa-offer-modal-label">Contract Offer</div>
          <div className="fa-offer-modal-player">
            <span className="ovr-badge" style={{ borderLeftColor: rColor }}>{ovr}</span>
            <div>
              <div className="fa-offer-modal-name">{fa.name}</div>
              <div className="fa-offer-modal-pos">{fa.position} · Market: {formatSalary(fa.aiOffers?.market?.mid)}/yr</div>
            </div>
          </div>
        </div>

        <div className="fa-offer-modal-body">
          {/* Signing Tool Selection */}
          <div className="fa-offer-section-title">Signing Method</div>
          <div className="fa-offer-tools">
            {signingOptions.tools.map(tool => (
              <button
                key={tool.type}
                className={`fa-offer-tool-btn ${selectedTool === tool.type ? 'active' : ''}`}
                onClick={() => {
                  setSelectedTool(tool.type);
                  if (salary > tool.maxSalary) setSalary(tool.maxSalary);
                  if (years > tool.maxYears) setYears(tool.maxYears);
                }}
              >
                <div className="fa-offer-tool-name">{tool.label}</div>
                <div className="fa-offer-tool-max">Up to {formatSalary(tool.maxSalary)}</div>
              </button>
            ))}
          </div>

          {/* Salary Slider */}
          <div className="fa-offer-section-title">Annual Salary</div>
          <div className="fa-slider-group">
            <input
              type="range"
              min={2100000}
              max={maxSalary}
              step={100000}
              value={clampedSalary}
              onChange={e => setSalary(parseInt(e.target.value))}
              className="fa-slider"
            />
            <div className="fa-slider-value">{formatSalaryFull(clampedSalary)}</div>
          </div>

          {/* Years */}
          <div className="fa-offer-section-title">Contract Length</div>
          <div className="fa-years-group">
            {[1, 2, 3, 4].map(y => (
              <button
                key={y}
                className={`fa-year-btn ${clampedYears === y ? 'active' : ''}`}
                onClick={() => setYears(y)}
                disabled={y > maxYears}
              >
                {y} yr{y > 1 ? 's' : ''}
              </button>
            ))}
          </div>

          {/* Offer Summary */}
          <div className="fa-offer-summary">
            <div className="fa-offer-summary-row">
              <span>Your Offer</span>
              <span className="fa-offer-summary-val">{formatSalary(clampedSalary)}/yr · {clampedYears}yr · {formatSalary(total)} total</span>
            </div>
            {bestOffer && (
              <div className="fa-offer-summary-row">
                <span>Best Competing Offer</span>
                <span className="fa-offer-summary-val">
                  {formatSalary(bestOffer.salary)}/yr · {bestOffer.years}yr · {formatSalary(bestOfferTotal)} total
                  <span style={{ marginLeft: 6, fontSize: 9, opacity: 0.7 }}>({teamName(bestOffer.team)})</span>
                </span>
              </div>
            )}
            <div className={`fa-offer-verdict ${willSign ? 'will-sign' : 'wont-sign'}`}>
              {willSign
                ? 'Player will accept your offer'
                : `Player prefers ${teamName(bestOffer?.team)}'s offer — increase to win`
              }
            </div>
          </div>
        </div>

        <div className="fa-offer-modal-actions">
          <button className="waive-cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className={`fa-offer-confirm-btn ${willSign ? '' : 'disabled'}`}
            onClick={() => willSign && onSign(fa.name, clampedSalary, clampedYears)}
            disabled={!willSign}
          >
            {willSign ? 'Sign Player' : 'Offer Too Low'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeAgencyBoard;
